import { Button } from "@faire/slate/src/components/button";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { shouldUseAnchorForLink } from "@faire/web/ui/lib";
import { Anchor, Link } from "@faire/web/ui/LinkAnchor";

import { handleAssignSettingAction } from "./__internal__/assignSetting";
import { handleLoggingAction } from "./__internal__/loggingAction";
import { getUrlFromOpenModalAction } from "./__internal__/openModalAction";

export const mapSDUIActionToButtonProps = (
  sduiActions: IAction[]
): Partial<React.ComponentProps<typeof Button>> => {
  const navigateAction = sduiActions.find((action) => action.navigate);
  const openModalLink = getUrlFromOpenModalAction(sduiActions);

  return {
    linkTo:
      navigateAction?.navigate?.new_tab_link ??
      navigateAction?.navigate?.link ??
      openModalLink,
    target: navigateAction?.navigate?.new_tab_link ? "_blank" : undefined,
    onClick: async () => {
      handleLoggingAction(sduiActions);
      return Promise.all([handleAssignSettingAction(sduiActions)]);
    },
  };
};

export const mapSDUIActionToPolymorphicProps = (sduiActions: IAction[]) => {
  // Filter out undefined values to avoid polymorphic type issues
  return Object.fromEntries(
    Object.entries(mapSDUIActionToButtonProps(sduiActions)).filter(
      ([_, value]) => value !== undefined
    )
  );
};

export const mapSDUIActionToAnchorProps = (
  sduiActions: IAction[]
): Partial<React.ComponentProps<typeof Anchor>> => {
  const navigateAction = sduiActions.find((action) => action.navigate);

  return {
    href:
      navigateAction?.navigate?.new_tab_link ?? navigateAction?.navigate?.link,
    onClick: () => {
      handleLoggingAction(sduiActions);
      handleAssignSettingAction(sduiActions);
    },
  };
};

export const mapSDUIActionToLinkProps = (
  sduiActions: IAction[]
): Partial<React.ComponentProps<typeof Link>> => {
  const navigateAction = sduiActions.find((action) => action.navigate);
  const openModalLink = getUrlFromOpenModalAction(sduiActions);

  if (
    shouldUseAnchorForLink(
      navigateAction?.navigate?.new_tab_link ?? navigateAction?.navigate?.link
    )
  ) {
    return {};
  }

  return {
    to:
      openModalLink ??
      navigateAction?.navigate?.new_tab_link ??
      navigateAction?.navigate?.link,
    target: navigateAction?.navigate?.new_tab_link ? "_blank" : undefined,
    onClick: () => {
      handleLoggingAction(sduiActions);
      handleAssignSettingAction(sduiActions);
    },
  };
};

export const mapSDUIActionToHTMLElementProps = (
  sduiActions: IAction[]
): Partial<React.HTMLAttributes<HTMLElement>> => {
  if (sduiActions.length === 0) {
    return {};
  }

  return {
    onClick: () => {
      handleLoggingAction(sduiActions);
      handleAssignSettingAction(sduiActions);
    },
  };
};
